import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { computeAlignmentSnap, snapToGrid, type AlignmentGuide } from './alignment';
import {
  applyGroupResize,
  applyResize,
  boundingBoxOf,
  clampSize,
  rectsIntersect,
  resolveCreatedRect,
  RESIZE_HANDLES,
  type Geometry,
  type LayoutObject,
  type ResizeHandle,
} from './types';

/** 参考线吸附阈值，用屏幕像素表达再按当前缩放换算成毫米，缩放越大阈值越"松"。 */
const SNAP_THRESHOLD_PX = 8;

export type SnapMode = 'off' | 'grid' | 'guides';

/**
 * 交互状态机（架构书 §10.2，M3b 扩展多选/框选/整体缩放）：
 *
 *   IDLE → (pointerdown 空白)              MARQUEE → IDLE
 *   IDLE → (pointerdown 对象)   MAYBE_DRAG → MOVING(单个或整个选区) → IDLE
 *   IDLE → (pointerdown 句柄)              RESIZING(8 向，单选或多选整体) → IDLE
 *   IDLE → (创建工具 + 拖出矩形)            CREATING → IDLE
 *   space/中键 + 拖动                       PANNING
 *   Ctrl/⌘ + 滚轮                           ZOOM（以光标为锚点）
 *   Esc（任意进行中的交互）                  → IDLE，丢弃瞬态草稿，不提交
 *
 * 拖拽期间几何只写在组件内的**瞬态状态**里（`draft`），不进服务端、不进
 * 全局缓存；抬手时才把最终结果交给 `onCommit`，由页面去建立撤销命令 + 走
 * debounce 批量保存。
 */
export type Interaction =
  | { readonly kind: 'idle' }
  | { readonly kind: 'panning'; readonly startX: number; readonly startY: number }
  | {
      readonly kind: 'marquee';
      readonly originX: number;
      readonly originY: number;
      readonly rect: { x: number; y: number; w: number; h: number };
      readonly additive: boolean;
    }
  | {
      readonly kind: 'moving';
      readonly targets: ReadonlyMap<string, Geometry>;
      readonly startX: number;
      readonly startY: number;
    }
  | {
      readonly kind: 'resizing';
      readonly locationId: string;
      readonly handle: ResizeHandle;
      readonly start: Geometry;
      readonly startX: number;
      readonly startY: number;
    }
  | {
      readonly kind: 'group-resizing';
      readonly handle: ResizeHandle;
      readonly box: { x: number; y: number; w: number; h: number };
      readonly targets: ReadonlyMap<string, Geometry>;
      readonly startX: number;
      readonly startY: number;
    }
  | {
      readonly kind: 'creating';
      readonly originX: number;
      readonly originY: number;
      readonly rect: { x: number; y: number; w: number; h: number };
    }
  | {
      readonly kind: 'pinching';
      readonly startDist: number;
      readonly startScale: number;
      readonly midX: number;
      readonly midY: number;
      readonly startOffsetX: number;
      readonly startOffsetY: number;
    };

export interface CanvasView {
  readonly scale: number;
  readonly offsetX: number;
  readonly offsetY: number;
}

export const DEFAULT_VIEW: CanvasView = { scale: 0.05, offsetX: 40, offsetY: 40 };

/**
 * 命令历史里存的几何快照：**只有几何值，绝不含 `version`**（M3B 独立复审
 * 第三轮的架构规则：历史快照不携带并发权威，version 一律在执行时刻由
 * `geometry-authority.ts` 的账本解析）。`locked` 是可选的——画布拖拽/缩放
 * 不触碰它，属性面板的锁定编辑会带上它。
 */
export interface GeometryChange {
  readonly locationId: string;
  readonly before: Pick<Geometry, 'x' | 'y' | 'w' | 'h'> & { readonly locked?: boolean };
  readonly after: Pick<Geometry, 'x' | 'y' | 'w' | 'h'> & { readonly locked?: boolean };
}

/**
 * setPointerCapture 在 pointerId 已失活时（快速抬手竞态、合成事件）会抛
 * NotFoundError，直接炸掉整个 pointerdown handler——捕获失败只是丢掉"指针
 * 移出元素仍收事件"的增强，不值得让交互死掉。
 */
function capturePointer(target: EventTarget | null, pointerId: number): void {
  try {
    (target as Element | null)?.setPointerCapture?.(pointerId);
  } catch {
    /* 指针已失活——放弃捕获即可 */
  }
}

export function LayoutCanvas({
  objects,
  geometryOf,
  selectedIds,
  conflictIds,
  editable,
  creating,
  snapMode,
  view,
  typeCodeById,
  focus,
  onViewChange,
  onSelect,
  onMarqueeSelect,
  onDrillIn,
  onCommit,
  onCreateRect,
}: {
  readonly objects: readonly LayoutObject[];
  readonly geometryOf: (o: LayoutObject) => Geometry;
  readonly selectedIds: ReadonlySet<string>;
  readonly conflictIds: ReadonlySet<string>;
  readonly editable: boolean;
  readonly creating: boolean;
  readonly snapMode: SnapMode;
  readonly view: CanvasView;
  /** 【用户设计】位置类型差异化渲染：typeId → 类型 code（区域/货架/冷库…） */
  readonly typeCodeById?: ReadonlyMap<string, string>;
  /** 【用户裁决】找货高亮 = 点击行对应位置的黄色边框；nonce 变化重放闪烁动画 */
  readonly focus?: { readonly id: string; readonly nonce: number } | null;
  readonly onViewChange: (view: CanvasView) => void;
  /** `additive` = Shift 点击：切换该对象是否在选区内，而不是替换整个选区 */
  readonly onSelect: (id: string | null, opts: { readonly additive: boolean }) => void;
  readonly onMarqueeSelect: (ids: readonly string[], opts: { readonly additive: boolean }) => void;
  readonly onDrillIn: (id: string) => void;
  readonly onCommit: (changes: readonly GeometryChange[]) => void;
  readonly onCreateRect: (rect: { x: number; y: number; w: number; h: number }) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [interaction, setInteraction] = useState<Interaction>({ kind: 'idle' });
  /** 拖拽/缩放期间的瞬态几何，只影响渲染，不写服务端。key 是 locationId。 */
  const [draft, setDraft] = useState<Map<string, Geometry> | null>(null);
  const [guides, setGuides] = useState<readonly AlignmentGuide[]>([]);
  // 【触屏】活动触点表——单指从空白处拖 = 平移，双指 = 捏合缩放。
  // 触屏没有中键也没有空格键，桌面那套"中键/空格+左键平移"在手机上完全
  // 不可达（实机反馈：不改缩放就根本看不到仓库）。touch-action:none 已由
  // CSS 关掉浏览器默认手势，这里自己实现。
  const touchPointers = useRef(new Map<number, { x: number; y: number }>());
  const [spaceHeld, setSpaceHeld] = useState(false);

  /** 屏幕像素增量 → 逻辑毫米增量 */
  const toMm = useCallback((px: number) => px / view.scale, [view.scale]);

  const clientToMm = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } => {
      const rect = surfaceRef.current?.getBoundingClientRect();
      return {
        x: Math.round((clientX - (rect?.left ?? 0) - view.offsetX) / view.scale),
        y: Math.round((clientY - (rect?.top ?? 0) - view.offsetY) / view.scale),
      };
    },
    [view],
  );

  // 空格键平移（架构书 §10.2 的 PANNING 入口之一）；Esc 取消进行中的交互
  useEffect(() => {
    const down = (e: KeyboardEvent): void => {
      if (e.code === 'Space' && !isTypingTarget(e.target)) {
        e.preventDefault();
        setSpaceHeld(true);
      }
      if (e.key === 'Escape') {
        // 【Esc 取消】只丢弃瞬态草稿，不调用 onCommit——正在进行的拖动/缩放/
        // 框选/创建像没发生过一样。已经落库的历史不受影响，撤销栈也不会
        // 多出一条记录。
        setInteraction({ kind: 'idle' });
        setDraft(null);
        setGuides([]);
      }
    };
    const up = (e: KeyboardEvent): void => {
      if (e.code === 'Space') setSpaceHeld(false);
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  const onWheel = (e: React.WheelEvent): void => {
    if (!(e.ctrlKey || e.metaKey)) {
      onViewChange({ ...view, offsetX: view.offsetX - e.deltaX, offsetY: view.offsetY - e.deltaY });
      return;
    }
    const rect = surfaceRef.current?.getBoundingClientRect();
    const px = e.clientX - (rect?.left ?? 0);
    const py = e.clientY - (rect?.top ?? 0);
    // 以光标为锚点缩放：锚点下的逻辑坐标在缩放前后保持不动
    const next = clampScale(view.scale * (e.deltaY < 0 ? 1.1 : 1 / 1.1));
    const ratio = next / view.scale;
    onViewChange({
      scale: next,
      offsetX: px - (px - view.offsetX) * ratio,
      offsetY: py - (py - view.offsetY) * ratio,
    });
  };

  const onSurfacePointerDown = (e: React.PointerEvent): void => {
    if (e.button !== 0 && e.button !== 1) return;
    capturePointer(e.target, e.pointerId);

    if (e.pointerType === 'touch') {
      touchPointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const pts = [...touchPointers.current.values()];
      if (pts.length === 2) {
        // 第二根手指落下 → 捏合缩放（以两指中点为锚）
        const [a, b] = pts as [{ x: number; y: number }, { x: number; y: number }];
        setInteraction({
          kind: 'pinching',
          startDist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
          startScale: view.scale,
          midX: (a.x + b.x) / 2,
          midY: (a.y + b.y) / 2,
          startOffsetX: view.offsetX,
          startOffsetY: view.offsetY,
        });
        return;
      }
      if (!creating) {
        // 单指从空白画布拖动 = 平移（触屏放弃框选——移动端选择靠点选）
        setInteraction({
          kind: 'panning',
          startX: e.clientX - view.offsetX,
          startY: e.clientY - view.offsetY,
        });
        return;
      }
    }

    if (creating && editable && !spaceHeld && e.button === 0) {
      const origin = clientToMm(e.clientX, e.clientY);
      setInteraction({
        kind: 'creating',
        originX: origin.x,
        originY: origin.y,
        rect: { ...origin, w: 0, h: 0 },
      });
      return;
    }
    // 中键 / 空格+左键 = 平移；纯左键点空白 = 框选（架构书 §10.2 状态机）
    if (e.button === 1 || spaceHeld) {
      setInteraction({
        kind: 'panning',
        startX: e.clientX - view.offsetX,
        startY: e.clientY - view.offsetY,
      });
      return;
    }
    const origin = clientToMm(e.clientX, e.clientY);
    setInteraction({
      kind: 'marquee',
      originX: origin.x,
      originY: origin.y,
      rect: { ...origin, w: 0, h: 0 },
      additive: e.shiftKey,
    });
  };

  const onObjectPointerDown = (e: React.PointerEvent, object: LayoutObject): void => {
    e.stopPropagation();
    const alreadyInSelection = selectedIds.has(object.locationId);
    if (e.shiftKey) {
      onSelect(object.locationId, { additive: true });
    } else if (!alreadyInSelection) {
      onSelect(object.locationId, { additive: false });
    }
    if (spaceHeld || creating || !editable) return;

    // 落在一个已经属于当前多选的对象上 → 整个选区一起拖；否则只拖这一个
    // （shift 点击刚触发的 additive 选择变化要到下一次渲染才反映在 props
    // 里，所以这里用 alreadyInSelection || 单选 兜底，取本次要拖的目标集合）
    const dragIds =
      alreadyInSelection && selectedIds.size > 1 ? [...selectedIds] : [object.locationId];
    const geometry = geometryOf(object);
    if (dragIds.length === 1 && geometry.locked) return;
    const targets = new Map<string, Geometry>();
    for (const id of dragIds) {
      const obj = objects.find((o) => o.locationId === id);
      if (obj && !geometryOf(obj).locked) targets.set(id, geometryOf(obj));
    }
    if (targets.size === 0) return;
    capturePointer(e.target, e.pointerId);
    setInteraction({ kind: 'moving', targets, startX: e.clientX, startY: e.clientY });
  };

  const onHandlePointerDown = (
    e: React.PointerEvent,
    object: LayoutObject,
    handle: ResizeHandle,
  ): void => {
    e.stopPropagation();
    if (!editable) return;
    capturePointer(e.target, e.pointerId);

    if (selectedIds.size > 1 && selectedIds.has(object.locationId)) {
      const targets = new Map<string, Geometry>();
      for (const id of selectedIds) {
        const obj = objects.find((o) => o.locationId === id);
        if (obj && !geometryOf(obj).locked) targets.set(id, geometryOf(obj));
      }
      if (targets.size < 2) return; // 全锁定了，退化不了，直接放弃这次操作
      setInteraction({
        kind: 'group-resizing',
        handle,
        box: boundingBoxOf([...targets.values()]),
        targets,
        startX: e.clientX,
        startY: e.clientY,
      });
      return;
    }

    const geometry = geometryOf(object);
    if (geometry.locked) return;
    onSelect(object.locationId, { additive: false });
    setInteraction({
      kind: 'resizing',
      locationId: object.locationId,
      handle,
      start: geometry,
      startX: e.clientX,
      startY: e.clientY,
    });
  };

  const onPointerMove = (e: React.PointerEvent): void => {
    if (e.pointerType === 'touch' && touchPointers.current.has(e.pointerId)) {
      touchPointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    if (interaction.kind === 'pinching') {
      const pts = [...touchPointers.current.values()];
      if (pts.length >= 2) {
        const [a, b] = pts as [{ x: number; y: number }, { x: number; y: number }];
        const dist = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
        const next = clampScale(interaction.startScale * (dist / interaction.startDist));
        const ratio = next / interaction.startScale;
        // 锚定捏合起始中点：中点对应的场景坐标在缩放前后保持不动
        onViewChange({
          scale: next,
          offsetX: interaction.midX - (interaction.midX - interaction.startOffsetX) * ratio,
          offsetY: interaction.midY - (interaction.midY - interaction.startOffsetY) * ratio,
        });
      }
      return;
    }
    switch (interaction.kind) {
      case 'panning':
        onViewChange({
          ...view,
          offsetX: e.clientX - interaction.startX,
          offsetY: e.clientY - interaction.startY,
        });
        break;
      case 'marquee': {
        const now = clientToMm(e.clientX, e.clientY);
        setInteraction({
          ...interaction,
          rect: {
            x: Math.min(interaction.originX, now.x),
            y: Math.min(interaction.originY, now.y),
            w: Math.abs(now.x - interaction.originX),
            h: Math.abs(now.y - interaction.originY),
          },
        });
        break;
      }
      case 'moving': {
        const rawDx = toMm(e.clientX - interaction.startX);
        const rawDy = toMm(e.clientY - interaction.startY);
        const startBox = boundingBoxOf([...interaction.targets.values()]);
        const { dx, dy, nextGuides } = resolveSnap(
          snapMode,
          startBox,
          rawDx,
          rawDy,
          objects.filter((o) => !interaction.targets.has(o.locationId)).map((o) => geometryOf(o)),
          toMm(SNAP_THRESHOLD_PX),
        );
        const next = new Map<string, Geometry>();
        for (const [id, start] of interaction.targets) {
          next.set(id, { ...start, x: Math.round(start.x + dx), y: Math.round(start.y + dy) });
        }
        setDraft(next);
        setGuides(nextGuides);
        break;
      }
      case 'resizing': {
        const dx = toMm(e.clientX - interaction.startX);
        const dy = toMm(e.clientY - interaction.startY);
        let result = applyResize(interaction.start, interaction.handle, dx, dy);
        if (snapMode === 'grid') result = snapResizeToGrid(result);
        setDraft(new Map([[interaction.locationId, { ...interaction.start, ...result }]]));
        break;
      }
      case 'group-resizing': {
        const dx = toMm(e.clientX - interaction.startX);
        const dy = toMm(e.clientY - interaction.startY);
        const scaled = applyGroupResize(
          interaction.box,
          interaction.handle,
          dx,
          dy,
          interaction.targets,
        );
        const next = new Map<string, Geometry>();
        for (const [id, start] of interaction.targets) {
          const s = scaled.get(id)!;
          next.set(id, { ...start, ...s });
        }
        setDraft(next);
        break;
      }
      case 'creating': {
        const now = clientToMm(e.clientX, e.clientY);
        // 【U-04】创建过程此前完全不吸附、也不显示参考线（只有拖动已有对象才有），
        // 用户拉新位置时没有任何对齐参考。这里复用与拖拽同一套 resolveSnap：把
        // **正在拖的那个角**当成一个零尺寸盒子去求吸附（零尺寸盒的 left/center/right
        // 同点，于是"角对齐到邻居的任意一条边"正是想要的语义），origin 角保持不动。
        const corner = resolveSnap(
          snapMode,
          { x: now.x, y: now.y, w: 0, h: 0 },
          0,
          0,
          objects.map((o) => geometryOf(o)),
          toMm(SNAP_THRESHOLD_PX),
        );
        const cx = now.x + corner.dx;
        const cy = now.y + corner.dy;
        setGuides(corner.nextGuides);
        setInteraction({
          ...interaction,
          rect: {
            x: Math.min(interaction.originX, cx),
            y: Math.min(interaction.originY, cy),
            w: Math.abs(cx - interaction.originX),
            h: Math.abs(cy - interaction.originY),
          },
        });
        break;
      }
      default:
        break;
    }
  };

  const onPointerUp = (e?: React.PointerEvent): void => {
    if (e && e.pointerType === 'touch') {
      touchPointers.current.delete(e.pointerId);
      if (interaction.kind === 'pinching') {
        // 一根手指抬起：若还剩一根，降级为平移接着拖；全抬起则回到 idle
        const rest = [...touchPointers.current.values()];
        if (rest.length === 1) {
          const p = rest[0]!;
          setInteraction({
            kind: 'panning',
            startX: p.x - view.offsetX,
            startY: p.y - view.offsetY,
          });
        } else {
          setInteraction({ kind: 'idle' });
        }
        return;
      }
    }
    if (interaction.kind === 'creating') {
      const { rect } = interaction;
      setInteraction({ kind: 'idle' });
      setGuides([]);
      // 【U-04】拖拽 → 用户拉出的尺寸；几乎无位移（点击）→ 默认尺寸方块。
      // 判定逻辑抽在 types.ts 的纯函数里，便于单测。
      onCreateRect(
        resolveCreatedRect({ x: interaction.originX, y: interaction.originY }, rect),
      );
      return;
    }
    if (interaction.kind === 'marquee') {
      const { rect, additive } = interaction;
      setInteraction({ kind: 'idle' });
      // 太小的框当成"点了一下空白"——不该清空已有选区之外还要求用户精确框住
      if (rect.w < 3 && rect.h < 3) {
        if (!additive) onSelect(null, { additive: false });
        return;
      }
      const hit = objects
        .filter((o) => rectsIntersect(rect, geometryOf(o)))
        .map((o) => o.locationId);
      onMarqueeSelect(hit, { additive });
      return;
    }
    const changes = computeCommitChanges(interaction, draft);
    if (changes.length > 0) onCommit(changes);
    setDraft(null);
    setGuides([]);
    setInteraction({ kind: 'idle' });
  };

  const effective = (object: LayoutObject): Geometry =>
    draft?.get(object.locationId) ?? geometryOf(object);

  const cursor = spaceHeld
    ? 'grab'
    : creating && editable
      ? 'crosshair'
      : interaction.kind === 'panning'
        ? 'grabbing'
        : 'default';

  const groupBox =
    selectedIds.size > 1
      ? boundingBoxOf(objects.filter((o) => selectedIds.has(o.locationId)).map((o) => effective(o)))
      : null;

  return (
    <div
      ref={surfaceRef}
      className="canvas"
      style={{ cursor }}
      onWheel={onWheel}
      onPointerDown={onSurfacePointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(e) => onPointerUp(e)}
      onPointerCancel={(e) => onPointerUp(e)}
    >
      <div
        className="canvas__world"
        style={{
          transform: `translate(${view.offsetX}px, ${view.offsetY}px) scale(${view.scale})`,
        }}
      >
        {[...objects]
          .sort((a, b) => effective(a).z - effective(b).z)
          .map((object) => {
            const g = effective(object);
            const selected = selectedIds.has(object.locationId);
            const soleSelected = selected && selectedIds.size === 1;
            const typeCode = object.typeId ? typeCodeById?.get(object.typeId) : undefined;
            const focused = focus?.id === object.locationId;
            return (
              <div
                key={object.locationId}
                className={[
                  'canvas__object',
                  selected ? 'canvas__object--selected' : '',
                  conflictIds.has(object.locationId) ? 'canvas__object--conflict' : '',
                  object.canStoreInventory ? '' : 'canvas__object--non-storage',
                  // 类型 code 只用白名单字符入 className，杜绝注入
                  typeCode ? `canvas__object--t-${typeCode.toLowerCase().replace(/[^a-z0-9-]/g, '')}` : '',
                  focused ? 'canvas__object--focus' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{
                  left: g.x,
                  top: g.y,
                  width: g.w,
                  height: g.h,
                  // 聚焦时不铺自定义色——淡黄聚焦底（CSS 类）必须可见，
                  // 否则内联背景会盖住"点了变色"的反馈
                  background: focused ? undefined : (g.color ?? undefined),
                }}
                onPointerDown={(e) => onObjectPointerDown(e, object)}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  onDrillIn(object.locationId);
                }}
                title={object.name}
              >
                {/* 【找货高亮】黄框环独立成子元素：key 挂 nonce，连续点击同一
                    位置也会重挂载、重放闪烁动画（用户反馈"点了没反应"） */}
                {focused && <span key={focus?.nonce} className="canvas__focus-ring" aria-hidden="true" />}

                {/* 逆缩放：标签始终以屏幕像素大小呈现，不随画布缩放变形 */}
                <span className="canvas__label" style={{ transform: `scale(${1 / view.scale})` }}>
                  {g.locked && <span aria-hidden="true">🔒 </span>}
                  {object.name}
                  {object.hasChildren && (
                    <span className="canvas__label-hint">{t('layout.drillHint')}</span>
                  )}
                </span>
                {/* 【用户反馈"名字下面太空"】名字下的贯穿分隔线。标签是屏幕
                    定长（约 24px 高），线的世界坐标随缩放换算，任何缩放级别
                    都稳定贴在名字下方、粗细恒为 ~1.5 屏幕像素 */}
                <span
                  className="canvas__object-rule"
                  aria-hidden="true"
                  style={{ top: 27 / view.scale, height: Math.max(1.5 / view.scale, 0.5) }}
                />

                {soleSelected && editable && !g.locked && (
                  <>
                    {RESIZE_HANDLES.map((handle) => (
                      <span
                        key={handle}
                        className={`canvas__handle canvas__handle--${handle}`}
                        style={{ transform: `scale(${1 / view.scale})` }}
                        onPointerDown={(e) => onHandlePointerDown(e, object, handle)}
                      />
                    ))}
                  </>
                )}
              </div>
            );
          })}

        {/* 多选整体包围盒：自己的一套缩放手柄，拖动即整体等比例缩放 */}
        {groupBox && editable && (
          <div
            className="canvas__group-box"
            style={{ left: groupBox.x, top: groupBox.y, width: groupBox.w, height: groupBox.h }}
          >
            {RESIZE_HANDLES.map((handle) => (
              <span
                key={handle}
                className={`canvas__handle canvas__handle--${handle}`}
                style={{ transform: `scale(${1 / view.scale})` }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  if (!editable) return;
                  capturePointer(e.target, e.pointerId);
                  const targets = new Map<string, Geometry>();
                  for (const id of selectedIds) {
                    const obj = objects.find((o) => o.locationId === id);
                    if (obj && !geometryOf(obj).locked) targets.set(id, geometryOf(obj));
                  }
                  if (targets.size < 2) return;
                  setInteraction({
                    kind: 'group-resizing',
                    handle,
                    box: groupBox,
                    targets,
                    startX: e.clientX,
                    startY: e.clientY,
                  });
                }}
              />
            ))}
          </div>
        )}

        {interaction.kind === 'creating' && (
          <div
            className="canvas__object canvas__object--ghost"
            style={{
              left: interaction.rect.x,
              top: interaction.rect.y,
              width: interaction.rect.w,
              height: interaction.rect.h,
            }}
          >
            {/* 【U-04】拖拽过程中的实时尺寸读数（毫米，与属性面板同单位）。
                此前拖到松手前没有任何尺寸参考，只能靠肉眼估。反向缩放抵消画布
                缩放，保证任何 zoom 下字号可读；松手前显示的就是最终尺寸。 */}
            <span
              className="canvas__size-badge"
              style={{ transform: `scale(${1 / view.scale})` }}
            >
              {Math.round(interaction.rect.w)} × {Math.round(interaction.rect.h)} mm
            </span>
          </div>
        )}

        {interaction.kind === 'marquee' && (
          <div
            className="canvas__marquee"
            style={{
              left: interaction.rect.x,
              top: interaction.rect.y,
              width: interaction.rect.w,
              height: interaction.rect.h,
            }}
          />
        )}

        {guides.map((guide, i) => (
          <div
            key={i}
            className={`canvas__guide canvas__guide--${guide.orientation}`}
            style={
              guide.orientation === 'v'
                ? { left: guide.pos, transform: `scaleX(${1 / view.scale})` }
                : { top: guide.pos, transform: `scaleY(${1 / view.scale})` }
            }
          />
        ))}
      </div>
    </div>
  );
}

/**
 * 从抬手那一刻的 `interaction`/`draft` 算出要提交的 `GeometryChange[]`——
 * 纯函数，抽出来单独导出是为了能在不挂载整个画布组件的情况下直接单测
 * （M3B 独立复审第二轮 NEW-1 的回归覆盖，见 `layout-canvas.test.ts`）。
 *
 * `before`/`after` 只挑 x/y/w/h：`start`（`interaction.targets` 的 value 或
 * `interaction.start`）运行时是完整的 `Geometry`，带着 drag 开始那一刻的
 * `version`——如果整个塞进 `before`，`applyGeometry` 在 undo 时用
 * `{...current, ...patch}` 合成要发的 version 就会被这个意外带着的旧
 * version 覆盖掉，undo 发出的 PATCH 因此带着一个已经过期的版本号，被服务端
 * 当成版本冲突拒绝——UI 已经视觉回滚，服务端却拒绝了这次写入，两边不一致。
 */
export function computeCommitChanges(
  interaction: Interaction,
  draft: ReadonlyMap<string, Geometry> | null,
): GeometryChange[] {
  if (
    (interaction.kind !== 'moving' &&
      interaction.kind !== 'resizing' &&
      interaction.kind !== 'group-resizing') ||
    !draft
  ) {
    return [];
  }
  const changes: GeometryChange[] = [];
  for (const [id, g] of draft) {
    const start = interaction.kind === 'resizing' ? interaction.start : interaction.targets.get(id);
    if (!start) continue;
    if (start.x === g.x && start.y === g.y && start.w === g.w && start.h === g.h) continue;
    changes.push({
      locationId: id,
      before: { x: start.x, y: start.y, w: start.w, h: start.h },
      after: { x: g.x, y: g.y, w: g.w, h: g.h },
    });
  }
  return changes;
}

/** grid/guides 两种吸附模式互斥，避免互相拉扯出用户看不懂的抖动（见 alignment.ts 顶部说明） */
function resolveSnap(
  mode: SnapMode,
  startBox: { x: number; y: number; w: number; h: number },
  rawDx: number,
  rawDy: number,
  others: readonly Geometry[],
  thresholdMm: number,
): { dx: number; dy: number; nextGuides: readonly AlignmentGuide[] } {
  if (mode === 'grid') {
    const x = snapToGrid(startBox.x + rawDx);
    const y = snapToGrid(startBox.y + rawDy);
    return { dx: x - startBox.x, dy: y - startBox.y, nextGuides: [] };
  }
  if (mode === 'guides') {
    const draggedBox = {
      x: startBox.x + rawDx,
      y: startBox.y + rawDy,
      w: startBox.w,
      h: startBox.h,
    };
    const snap = computeAlignmentSnap(draggedBox, others, thresholdMm);
    return { dx: snap.x - startBox.x, dy: snap.y - startBox.y, nextGuides: snap.guides };
  }
  return { dx: rawDx, dy: rawDy, nextGuides: [] };
}

function snapResizeToGrid(result: { x: number; y: number; w: number; h: number }): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  const x = snapToGrid(result.x);
  const y = snapToGrid(result.y);
  const right = snapToGrid(result.x + result.w);
  const bottom = snapToGrid(result.y + result.h);
  return { x, y, w: clampSize(right - x), h: clampSize(bottom - y) };
}

export function clampScale(scale: number): number {
  return Math.min(1, Math.max(0.004, scale));
}

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}
